import { AxiosError } from 'axios'
import { action, computed, flow, makeAutoObservable, observable } from 'mobx'

import { openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { DataReport } from 'Core/common/DataReport/types'
import { formatReportParameters } from 'Core/common/DataReport/utils/helpers'
import { Item } from 'Core/common/ReportCard/types'
import { STORE_UI } from 'Core/constants'
import { CustomGenerator, LoadingBtnNames } from 'Core/interfaces'
import {
	formatDataToLabelValue,
	formatValueDescriptionDataToLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { Report, ReportBasicFields } from 'Portals/Customer/entities'
import { ReportRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import i18n from 'Portals/Customer/utils/i18nService'

import { ReportTable } from './ReportTable'

export class ReportStore {
	table: ReportTable
	rootStore: RootStore
	@observable lastReports = getDefaultStoreValue<{ items: Report.Basic[]; total: number }>()
	@observable isReportLogDownloading: boolean = false
	@observable reportNames = getDefaultStoreArrayValue()
	@observable reportData = getDefaultStoreValue<DataReport.Data>()

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
		this.table = new ReportTable(rootStore)
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@computed
	get reportNamesOptions() {
		return formatDataToLabelValue(this.reportNames.value)
	}

	@flow
	*retrieveLastReports(): CustomGenerator<{ items: Report.Basic[]; total: number }> {
		yield requestWrapper({
			field: this.lastReports,
			repository: ReportRepo.getList,
			repositoryParams: { page: 1 }
		})
	}

	@computed
	get formattedTreatmentMethodsMostUsed(): Item[] {
		const MAX_PAGE_SIZE = 4
		return (
			this.lastReports.value?.items
				.map((item) => ({
					id: item[ReportBasicFields.reportLogId],
					name: item[ReportBasicFields.reportName],
					status: item[ReportBasicFields.status]
				}))
				?.slice(0, MAX_PAGE_SIZE) || []
		)
	}

	@flow
	*downloadReportLog(reportLogId: Key | null): CustomGenerator<Blob> {
		if (!reportLogId) {
			return
		}
		this.isReportLogDownloading = true

		try {
			const document = yield ReportRepo.downloadReport(reportLogId)
			openPdfDocumentFromBlob(document, reportLogId.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isReportLogDownloading = false
		}
	}

	@flow
	*retrieveReportNameOptions(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.reportNames,
			repository: ReportRepo.retrieveReportNameOptions,
			repositoryParams: null
		})
	}

	@flow
	*retrieveReportData(id: number): CustomGenerator<DataReport.Detailed> {
		this.reportData.isLoading = true
		try {
			const report = yield ReportRepo.retrieveReportData(id)

			this.reportData.value = {
				...report,
				reportWhereParameters: formatReportParameters(
					report.reportWhereParameters
				) as DataReport.ReportWhereParameters[],
				sqlParameters: formatReportParameters(
					report.sqlParameters
				) as DataReport.SqlParameters[]
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			this.clearReportData()
		}
		this.reportData.isLoading = false
	}

	@flow
	*retrievePreviewOptions(
		parameterId: Key,
		query?: string
	): CustomGenerator<ValueDescription[], LabelValue[] | undefined> {
		try {
			const options = yield ReportRepo.retrievePreviewOptions(parameterId, query)
			return formatValueDescriptionDataToLabelValue(options)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow
	*generateReport(reportId: number, data: DataReport.Request): CustomGenerator<void> {
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.generateReport, true)
		try {
			yield ReportRepo.generateReport(reportId, data)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:GENERATE_REPORT_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@flow
	*generateDataEmailReport(
		reportId: number,
		reportData: DataReport.Request
	): CustomGenerator<void | string, void> {
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.sendReportToEmail)
		try {
			yield ReportRepo.generateEmailReport(reportId, reportData)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:GENERATE_EMAIL_REPORT_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@action
	clearReportData() {
		this.reportData = getDefaultStoreValue()
	}
}
